import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type { Query } from 'graphql-models';
import type AppShipmentsIndexController from 'walden-trace-app/controllers/app/shipments';
import shipmentsQuery from 'walden-trace-app/graphql/shipments/shipments.graphql';
import type ApiService from 'walden-trace-app/services/api';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppShipmentsModel = Resolved<ReturnType<AppShipmentsRoute['model']>>;

export default class AppShipmentsRoute extends Route {
  @service('api') declare apiService: ApiService;
  @service('router') declare routerService: RouterService;

  queryParams = {
    search: {
      refreshModel: true,
    },
    shipmentType: {
      refreshModel: true,
    },
    shipmentState: {
      refreshModel: true,
    },
    fromDate: {
      refreshModel: true,
    },
    showAll: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
  };

  shipmentsController: AppShipmentsIndexController | undefined;

  setupController(controller: AppShipmentsIndexController, ...args: unknown[]) {
    // @ts-expect-error the controller spread type is not well typed in Ember
    super.setupController(controller, ...args);
    this.shipmentsController = controller;
  }

  beforeModel(transition: Transition) {
    const { showAll, shipmentState, ...queryParams } = transition.to?.queryParams || {};

    if (!shipmentState || shipmentState !== showAll) {
      // reset the show all setting when choosing another state filter because otherwise there is no way to clear this showAll and it will always fetch all shipments for this state
      return this.routerService.replaceWith('app.shipments', {
        queryParams: { ...queryParams, showAll: undefined, shipmentState },
      });
    }
  }

  async model({
    search,
    shipmentType,
    fromDate,
    toDate,
    showAll,
  }: {
    search: string;
    shipmentType: string;
    fromDate: string;
    toDate: string;
    showAll: string;
  }) {
    if (this.shipmentsController) {
      this.shipmentsController.isLoading = true;
    }

    const generalFilter = { search, shipmentType, fromDate, toDate };

    const data = await this.apiService.query<{
      outForDelivery: Query['shipmentsForConsignee'];
      planned: Query['shipmentsForConsignee'];
      delivered: Query['shipmentsForConsignee'];
      inTransit: Query['shipmentsForConsignee'];
    }>({
      query: shipmentsQuery,
      variables: {
        outForDeliveryFirst: showAll === 'outForDelivery' ? 1000 : 5,
        outForDeliveryFilter: {
          ...generalFilter,
          state: ['TRANSPORT_ORDER_RECEIVED'],
        },
        plannedFirst: showAll === 'planned' ? 1000 : 5,
        plannedFilter: {
          ...generalFilter,
          state: ['ORDER_RECEIVED', 'ORDER_PACKED'],
        },
        deliveredFirst: showAll === 'delivered' ? 1000 : 5,
        deliveredFilter: { ...generalFilter, state: ['DELIVERED'] },
        inTransitFirst: showAll === 'inTransit' ? 1000 : 5,
        inTransitFilter: { ...generalFilter, state: ['SHIPPING_IN_PROGRESS'] },
      },
    });

    if (this.shipmentsController) {
      this.shipmentsController.isLoading = false;
    }

    return data;
  }
}
